import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Globe, Bot, Users, Server, Clock } from 'lucide-react';
import { cn } from '@/lib/cn';
import { opsApi } from '@/lib/ops-api';
import { usePolling } from '@/hooks/usePolling';
import StatusBadge, { deriveStatus } from '@/components/StatusBadge';
import ServiceRow from '@/components/ServiceRow';
import type { TeamDetailResponse } from '@/lib/types';

export default function TeamDetailPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();

  const { data, error, loading, refresh } = usePolling<TeamDetailResponse>(
    () => opsApi.getTeamDetail(teamId!),
    30_000,
    !!teamId,
  );

  if (!teamId) {
    return <div className="text-surface-400">No team specified</div>;
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 rounded-lg bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] transition-colors"
          >
            <ArrowLeft className="w-4 h-4 text-surface-400" />
          </button>
          <div>
            {loading && !data ? (
              <div className="space-y-2">
                <div className="h-7 w-32 bg-surface-700 rounded animate-pulse" />
                <div className="h-4 w-48 bg-surface-800 rounded animate-pulse" />
              </div>
            ) : data ? (
              <>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-display font-bold text-white">
                    {data.team.team_id}
                  </h1>
                  <StatusBadge
                    status={deriveStatus(
                      data.summary.healthy_services,
                      data.summary.unhealthy_services,
                      data.summary.service_count,
                    )}
                    size="sm"
                  />
                </div>
                <div className="flex items-center gap-2 text-surface-500 text-sm mt-1">
                  <Globe className="w-3.5 h-3.5" />
                  <span className="font-mono">
                    {data.team.domain || `${data.team.team_id}.kaiandco.ai`}
                  </span>
                </div>
              </>
            ) : (
              <h1 className="text-2xl font-display font-bold text-white">{teamId}</h1>
            )}
          </div>
        </div>

        <button
          onClick={refresh}
          disabled={loading}
          className={cn(
            'flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-surface-400',
            'bg-white/[0.03] border border-white/[0.06]',
            'hover:text-surface-200 hover:bg-white/[0.05] transition-all',
            'disabled:opacity-50',
          )}
        >
          <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-300 text-sm">
          {error}
        </div>
      )}

      {loading && !data ? (
        <DetailSkeleton />
      ) : data ? (
        <>
          {/* Summary stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={<Server className="w-4 h-4" />} label="Services" value={data.summary.service_count} />
            <StatCard icon={<Bot className="w-4 h-4" />} label="Agents" value={data.summary.agent_count} />
            <StatCard icon={<Users className="w-4 h-4" />} label="Members" value={data.summary.member_count} />
            <StatCard icon={<Clock className="w-4 h-4" />} label="Tasks" value={data.summary.task_count} />
          </div>

          {/* Services */}
          <Section title="Services" count={data.services.length}>
            {data.services.length > 0 ? (
              <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
                {data.services.map((svc) => (
                  <ServiceRow key={svc.full_name} service={svc} />
                ))}
              </div>
            ) : (
              <EmptyState text="No services running" />
            )}
          </Section>

          {/* Agents */}
          <Section title="Agents" count={data.agents.length}>
            {data.agents.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {data.agents.map((agent) => (
                  <div
                    key={agent.agent_id}
                    className="flex items-center gap-3 bg-white/[0.02] border border-white/[0.06] rounded-xl px-4 py-3"
                  >
                    <div className={cn(
                      'w-8 h-8 rounded-lg flex items-center justify-center text-xs font-display font-bold',
                      agentColor(agent.agent_id),
                    )}>
                      {agent.agent_id.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-white text-sm font-medium capitalize truncate">
                        {agent.agent_id}
                      </p>
                      <p className={cn(
                        'text-xs',
                        agent.status === 'running' ? 'text-status-healthy' : 'text-surface-500',
                      )}>
                        {agent.status || 'installed'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState text="No agents installed" />
            )}
          </Section>

          {/* Members */}
          <Section title="Members" count={data.members.length}>
            {data.members.length > 0 ? (
              <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden divide-y divide-white/[0.04]">
                {data.members.map((member) => (
                  <div
                    key={member.user_id}
                    className="flex items-center justify-between px-4 py-3"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-surface-700 flex items-center justify-center text-xs font-semibold text-surface-300">
                        {member.display_name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium truncate">{member.display_name}</p>
                        <p className="text-surface-600 text-xs font-mono truncate">{member.email}</p>
                      </div>
                    </div>
                    <span className="text-xs text-surface-500 bg-surface-800 px-2 py-0.5 rounded">
                      {member.role}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState text="No members" />
            )}
          </Section>

          {/* Tasks (running ECS tasks) */}
          {data.tasks.length > 0 && (
            <Section title="Running Tasks" count={data.tasks.length}>
              <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden divide-y divide-white/[0.04]">
                {data.tasks.map((task) => (
                  <div key={task.task_id} className="px-4 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-xs font-mono text-surface-500 truncate">
                          {task.task_id.substring(0, 12)}...
                        </span>
                        <span className={cn(
                          'text-xs px-1.5 py-0.5 rounded',
                          task.status === 'RUNNING'
                            ? 'bg-status-healthy/15 text-status-healthy'
                            : 'bg-surface-700 text-surface-400',
                        )}>
                          {task.status}
                        </span>
                      </div>
                      {task.health_status && (
                        <span className={cn(
                          'text-xs',
                          task.health_status === 'HEALTHY' ? 'text-status-healthy' : 'text-status-warning',
                        )}>
                          {task.health_status}
                        </span>
                      )}
                    </div>
                    {task.containers.length > 0 && (
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {task.containers.map((c, i) => (
                          <span
                            key={i}
                            className={cn(
                              'text-[10px] font-mono px-1.5 py-0.5 rounded border',
                              c.status === 'RUNNING'
                                ? 'bg-status-healthy/10 border-status-healthy/20 text-status-healthy'
                                : 'bg-surface-800 border-surface-700 text-surface-500',
                            )}
                          >
                            {c.name}: {c.status}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}
        </>
      ) : null}
    </div>
  );
}

function Section({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-sm font-medium text-surface-400 uppercase tracking-wider mb-3">
        {title}
        <span className="text-surface-600 ml-2">({count})</span>
      </h2>
      {children}
    </div>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="metric-card flex items-center gap-3">
      <span className="text-surface-500">{icon}</span>
      <div>
        <p className="text-xl font-display font-bold text-white leading-none">{value}</p>
        <p className="text-xs text-surface-500 mt-0.5">{label}</p>
      </div>
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="text-center py-8 text-surface-600 text-sm">{text}</div>
  );
}

function agentColor(agentId: string): string {
  const colors: Record<string, string> = {
    kai: 'bg-agent-kai/20 text-agent-kai',
    mia: 'bg-agent-mia/20 text-agent-mia',
    finn: 'bg-agent-finn/20 text-agent-finn',
    sam: 'bg-agent-sam/20 text-white',
    luna: 'bg-agent-luna/20 text-agent-luna',
  };
  return colors[agentId] || 'bg-primary-500/20 text-primary-400';
}

function DetailSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="metric-card">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-surface-700 rounded" />
              <div className="space-y-1.5">
                <div className="h-5 w-8 bg-surface-700 rounded" />
                <div className="h-3 w-14 bg-surface-800 rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div>
        <div className="h-4 w-24 bg-surface-800 rounded mb-3" />
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="w-4 h-4 bg-surface-700 rounded" />
              <div className="h-4 flex-1 bg-surface-800 rounded" />
              <div className="h-6 w-16 bg-surface-700 rounded-full" />
              <div className="h-4 w-16 bg-surface-800 rounded" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
